import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator, 
  Workflow, 
  Zap, 
  DollarSign, 
  FileCheck, 
  Shield, 
  Briefcase, 
  Award,
  ArrowRight,
  Brain,
  FileText,
  MessageSquare,
  TrendingUp,
  CheckCircle,
  Globe
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Calculator className="w-12 h-12" />,
      title: 'Bookkeeping & Accounting',
      description: 'Professional financial management to keep your business organized, compliant, and profitable.',
      features: [
        'Financial statement preparation',
        'General ledger maintenance',
        'Accounts payable/receivable',
        'Monthly reconciliations'
      ]
    },
    {
      icon: <Workflow className="w-12 h-12" />,
      title: 'Workflow Mapping & Analysis',
      description: 'Identify bottlenecks and inefficiencies in your current workflows to optimize business operations.',
      features: [
        'Process documentation',
        'Bottleneck identification',
        'Efficiency recommendations',
        'Implementation support'
      ]
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Business Automation',
      description: 'Automate repetitive, rule-based tasks to free up your team for high-value work.',
      features: [
        'Task automation solutions',
        'Custom workflow creation',
        'Integration with existing tools',
        'Training and support'
      ]
    },
    {
      icon: <DollarSign className="w-12 h-12" />,
      title: 'Payroll Services',
      description: 'Comprehensive payroll management ensuring accuracy, compliance, and timely payment processing.',
      features: [
        'Payroll processing',
        'Tax filing and compliance',
        'Direct deposit setup',
        'Employee portal access'
      ]
    },
    {
      icon: <FileCheck className="w-12 h-12" />,
      title: 'Notary Services',
      description: 'Professional notary services for all your document authentication and verification needs.',
      features: [
        'Document notarization',
        'Mobile notary options',
        'Legal document preparation',
        'Apostille services'
      ]
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Estate Planning',
      description: 'Simplified estate planning services to protect your legacy and ensure your wishes are honored.',
      features: [
        'Will preparation',
        'Trust establishment',
        'Power of attorney',
        'Healthcare directives'
      ]
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: 'Insurance Solutions',
      description: 'Affordable insurance options tailored to protect your business and personal assets.',
      features: [
        'Business insurance',
        'Life insurance',
        'Health insurance',
        'Policy reviews'
      ]
    },
    {
      icon: <Briefcase className="w-12 h-12" />,
      title: 'Business Essentials',
      description: 'Comprehensive business support services to help your company thrive and grow.',
      features: [
        'Business formation',
        'Compliance consulting',
        'Strategic planning',
        'Ongoing advisory'
      ]
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: 'Website Building & Design',
      description: 'Professional website design and development services to establish your online presence and grow your digital footprint.',
      features: [
        'Custom website design',
        'Responsive mobile-friendly layouts',
        'SEO optimization',
        'Ongoing maintenance & support'
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="services-hero-title">
            Our Professional Services
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-testid="services-hero-description">
            Comprehensive solutions designed to streamline your operations, boost productivity, and drive growth.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="text-blue-800">{service.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-900 ml-4">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-800 rounded-full mr-3"></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-20" data-testid="value-propositions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-blue-50 rounded-xl" data-testid="value-prop-1">
              <div className="text-5xl mb-4">🚀</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Elevate Your Business
              </h3>
              <p className="text-gray-600">
                From streamlining workflows to boosting productivity, our comprehensive solutions empower you to achieve your goals.
              </p>
            </div>
            <div className="text-center p-8 bg-blue-50 rounded-xl" data-testid="value-prop-2">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Focus on Your Vision
              </h3>
              <p className="text-gray-600">
                We handle the complexities of business operations, allowing you to focus on your core vision and growth.
              </p>
            </div>
            <div className="text-center p-8 bg-blue-50 rounded-xl" data-testid="value-prop-3">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Data-Driven Decisions
              </h3>
              <p className="text-gray-600">
                Harness the power of data to make intelligent decisions and drive sustainable business growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Automation Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50" data-testid="ai-automation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
              <Brain className="w-12 h-12 text-blue-800" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Approach to AI Automation
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              We combine cutting-edge artificial intelligence with process automation to create smart, efficient, and scalable solutions for your business. Our AI-powered systems learn, adapt, and make intelligent decisions to streamline complex workflows across every department.
            </p>
          </div>

          {/* AI Services */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">AI Automation Services</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Service 1 */}
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-indigo-600">
                <FileText className="w-12 h-12 text-indigo-600 mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  Intelligent Document Processing
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Automate the extraction, validation, and routing of information from unstructured documents like invoices and forms using NLP and machine learning.
                </p>
              </div>

              {/* Service 2 */}
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-blue-600">
                <MessageSquare className="w-12 h-12 text-blue-600 mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  AI Customer Support Agents
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Deploy conversational AI agents that understand customer intent and provide personalized, 24/7 support, freeing up your human teams for complex issues.
                </p>
              </div>

              {/* Service 3 */}
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-purple-600">
                <TrendingUp className="w-12 h-12 text-purple-600 mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  Predictive Demand Forecasting
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Leverage AI-driven analytics to accurately predict customer demand and market shifts, optimizing your inventory and supply chain management.
                </p>
              </div>

            </div>
          </div>

          {/* Case Study / Results */}
          <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
            <div className="flex items-start space-x-4 mb-6">
              <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Case Study: Transformative Results</h3>
                <p className="text-lg md:text-xl text-blue-100 leading-relaxed mb-6">
                  See how we helped a client save an estimated <span className="font-bold text-white">44,000 hours</span> and <span className="font-bold text-white">$6.9 million</span> by automating their screening processes using our custom AI solutions.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="text-4xl font-bold text-green-400 mb-2">44,000+</div>
                    <div className="text-blue-200">Hours Saved</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="text-4xl font-bold text-green-400 mb-2">$6.9M</div>
                    <div className="text-blue-200">Cost Savings</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-800 text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="services-cta-title">
            Let's Discuss Your Needs
          </h2>
          <p className="text-xl mb-8 text-blue-100" data-testid="services-cta-description">
            Contact us today to learn how our services can be customized for your business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            data-testid="services-cta-button"
          >
            Contact Us
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;